/**
 * Dual Tracking: GTM/dataLayer + Supabase Real-Time
 *
 * Esta capa envía CADA evento a:
 *   1. window.dataLayer (consumido por GTM → GA4)
 *   2. Supabase site_events (consumido por el panel admin)
 *
 * Los nombres de eventos están unificados con @/utils/analytics
 * para evitar duplicados (ej: form_submit, NO form_submit_success).
 */

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

// ============================================
// HELPERS
// ============================================
import { getAttribution } from "@/lib/attribution";

const TRACKING_ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-site-event`;

const getVisitorId = (): string => {
  const key = '_vid';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
};

const getSessionId = (): string => {
  const key = '_sid';
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(key, id);
  }
  return id;
};

const getUTMParams = () => {
  // First-touch: lee de sessionStorage; si no, de URL actual.
  const a = getAttribution();
  return {
    utm_source: a.utm_source,
    utm_medium: a.utm_medium,
    utm_campaign: a.utm_campaign,
    utm_content: a.utm_content,
    utm_term: a.utm_term,
  };
};

// ============================================
// CORE DUAL TRACKING
// ============================================
const sendToSupabase = (
  eventName: string,
  eventCategory: string,
  eventParams: Record<string, unknown> = {}
) => {
  try {
    const utms = getUTMParams();
    const payload = JSON.stringify({
      event_name: eventName,
      event_category: eventCategory,
      event_params: eventParams,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
      session_id: getSessionId(),
      visitor_id: getVisitorId(),
      screen_width: window.innerWidth,
      ...utms,
    });

    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(TRACKING_ENDPOINT, blob);
    } else {
      fetch(TRACKING_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Silent fail
  }
};

const sendDataLayer = (eventName: string, params?: Record<string, unknown>) => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName, ...(params || {}) });
};

/**
 * Envía un evento simultáneamente a dataLayer (GTM/GA4) y Supabase (admin).
 * Exportado para uso desde @/utils/analytics si se requiere persistencia en BD.
 */
export const trackDual = (
  eventName: string,
  eventCategory: string,
  params: Record<string, unknown> = {}
) => {
  sendDataLayer(eventName, params);
  sendToSupabase(eventName, eventCategory, params);
};

// ============================================
// PUBLIC TRACKING FUNCTIONS — nombres canónicos
// (alineados con @/utils/analytics)
// ============================================

// Page views
export const trackPageViewFamilias = () => {
  trackDual('page_view_landing_familias', 'navigation');
};

export const trackPageView = (pageName: string) => {
  trackDual('page_view', 'navigation', { page_name: pageName });
};

// Hero CTA
export const trackHeroCtaClick = () => {
  trackDual('cta_click', 'engagement', { cta_location: 'hero', cta_text: 'Participa' });
};

// Form lifecycle
export const trackFormStart = () => {
  trackDual('form_start', 'form', { form_name: 'concurso_participacion' });
};

export const trackFormStepView = (stepNumber: number, stepName: string) => {
  trackDual('form_step_view', 'form', {
    form_name: 'concurso_participacion',
    step_number: stepNumber,
    step_name: stepName,
  });
};

export const trackFormStepNext = (stepNumber: number, stepName: string) => {
  trackDual('form_step_next', 'form', { step_number: stepNumber, step_name: stepName });
};

export const trackFormStepBack = (stepNumber: number, stepName: string) => {
  trackDual('form_step_back', 'form', { step_number: stepNumber, step_name: stepName });
};

/**
 * Antes era `form_field_interaction` (onFocus). Ahora unificado a
 * `form_field_complete` y debe llamarse en onBlur cuando el campo tenga valor.
 */
export const trackFieldComplete = (fieldName: string, stepNumber: number) => {
  trackDual('form_field_complete', 'form', {
    form_name: 'concurso_participacion',
    field_name: fieldName,
    step_number: stepNumber,
  });
};

export const trackFormCheckboxToggle = (checkboxName: string, checked: boolean) => {
  trackDual('form_checkbox_toggle', 'form', { checkbox_name: checkboxName, checked });
};

/**
 * Único evento de envío exitoso. Reemplaza a `form_submit_success` y `submit_form`.
 */
export const trackFormSubmit = (method: string = 'web') => {
  trackDual('form_submit', 'form', {
    form_name: 'concurso_participacion',
    submission_method: method,
  });
};

export const trackFormError = (errorType: string, fieldName?: string) => {
  trackDual('form_error', 'form', {
    form_name: 'concurso_participacion',
    error_type: errorType,
    error_field: fieldName || 'unknown',
  });
};

export const trackFormStepTime = (stepNumber: number, stepName: string, durationSeconds: number) => {
  trackDual('form_step_time', 'form', {
    step_number: stepNumber,
    step_name: stepName,
    duration_seconds: durationSeconds,
  });
};

export const trackFormTotalTime = (durationSeconds: number) => {
  trackDual('form_total_time', 'form', { duration_seconds: durationSeconds });
};

/**
 * Abandono del formulario: el usuario salió o cambió de pestaña sin enviar.
 * Se dispara desde FamilyForm en visibilitychange === 'hidden'.
 */
export const trackFormAbandon = (lastStep: number, fieldsCompleted: number) => {
  trackDual('form_abandon', 'form', {
    form_name: 'concurso_participacion',
    last_field: `step_${lastStep}`,
    fields_completed: fieldsCompleted,
  });
};

// Engagement
export const trackTermsClick = (location: 'header' | 'form' | 'footer') => {
  trackDual('terms_click', 'engagement', { location });
};

export const trackFaqOpen = (faqQuestion: string) => {
  trackDual('faq_click', 'engagement', { faq_question: faqQuestion });
};

// ============================================
// AUTOMATIC LISTENERS (consumidos por useAnalytics)
// Migrados desde el antiguo @/utils/analytics
// ============================================

export const trackSectionView = (sectionName: string) => {
  trackDual('section_view', 'engagement', { section_name: sectionName });
};

export const trackScrollDepth = (percent: number) => {
  trackDual('scroll_milestone', 'engagement', { scroll_percent: percent });
};

export const trackEngagementTime = (seconds: number) => {
  trackDual('engagement_timer', 'engagement', { time_on_page_seconds: seconds });
};

/**
 * Filtros de ruido típicos en GA4 para no inflar js_error con eventos
 * que no podemos accionar (cross-origin scripts, ResizeObserver loop, etc).
 */
const NOISE_PATTERNS = [
  /^Script error\.?$/i,
  /^ResizeObserver loop/i,
  /Non-Error promise rejection captured/i,
  /Load failed$/i, // Safari fetch genérico
  /NetworkError when attempting to fetch/i,
  /The operation was aborted/i,
];

const NOISE_SOURCES = [
  /googletagmanager\.com/i,
  /google-analytics\.com/i,
  /doubleclick\.net/i,
  /facebook\.net/i,
  /facebook\.com/i,
  /clarity\.ms/i,
  /hotjar/i,
];

export const shouldIgnoreJsError = (message: string, source: string): boolean => {
  const m = String(message || "").trim();
  if (!m || m === "null" || m === "undefined") return true;
  if (NOISE_PATTERNS.some((rx) => rx.test(m))) return true;
  if (source && NOISE_SOURCES.some((rx) => rx.test(source))) return true;
  return false;
};

export const trackJsError = (errorMessage: string, errorSource: string) => {
  if (shouldIgnoreJsError(errorMessage, errorSource)) return;
  const src = String(errorSource).substring(0, 200);
  trackDual('js_error', 'error', {
    error_message: String(errorMessage).substring(0, 200),
    error_source: src,
    error_filename: src, // alias para dimensiones de GA4 que usan error_filename
  });
};

/**
 * Solo dispara campaign_landing si la URL ACTUAL trae UTMs (entrada real).
 * El first-touch persistente lo maneja initAttribution + getAttribution.
 */
export const trackCampaignLanding = () => {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const utmData: Record<string, string> = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((key) => {
    const val = params.get(key);
    if (val) utmData[key] = val;
  });
  if (Object.keys(utmData).length > 0) {
    trackDual('campaign_landing', 'acquisition', utmData);
  }
};

// Scroll depth tracker (versión interna usada por initScrollDepthTracking)
let firedThresholds = new Set<number>();
export const initScrollDepthTracking = () => {
  firedThresholds = new Set<number>();
  const thresholds = [25, 50, 75, 90];

  const handler = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) return;
    const percent = Math.round((scrollTop / docHeight) * 100);

    thresholds.forEach((t) => {
      if (percent >= t && !firedThresholds.has(t)) {
        firedThresholds.add(t);
        trackDual('scroll_milestone', 'engagement', { scroll_percent: t });
      }
    });
  };

  window.addEventListener('scroll', handler, { passive: true });
  return () => window.removeEventListener('scroll', handler);
};
